import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Helper/Pagination";
import { deleteRegion, fetchRegionPages } from "../../HTTP/countries";

const ITEMS_PER_PAGE = 20;

const getRegionError = (error) => {
    if (error?.status === 400) return "Le nom et la liste des pays sont invalides.";
    if (error?.status === 403) return "Vous n'avez pas la permission de gérer les régions.";
    return error?.message || "Impossible de charger les régions.";
};

export default function RegionsAdmin() {
    const localUser = JSON.parse(localStorage.getItem("user") || "null");
    const hasRegionPermission = Boolean(localUser) &&
        (Number(localUser.super_admin) === 1 || Number(localUser.admin_access) === 5);
    const navigate = useNavigate();
    const [regions, setRegions] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [query, setQuery] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!hasRegionPermission) {
            window.location.href = "/";
        }
    }, [hasRegionPermission]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError("");

        fetchRegionPages((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE, query)
            .then((response) => {
                if (!isMounted) return;
                setRegions(Array.isArray(response?.data) ? response.data : []);
                setCount(Number(response?.count) || 0);
            })
            .catch((loadError) => {
                console.error("Erreur lors du chargement des régions", loadError);
                if (isMounted) setError(getRegionError(loadError));
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [page, query, refresh]);

    const handleSearch = (event) => {
        event.preventDefault();
        setPage(1);
        setQuery(searchInput.trim());
    };

    const handleDelete = async (region) => {
        if (!window.confirm(`Supprimer la région « ${region.name} » ?`)) return;

        setError("");
        try {
            const response = await deleteRegion(region.id);
            if (Number(response?.result) !== 1) {
                throw new Error("La région n'a pas été supprimée.");
            }
            setRefresh((current) => current + 1);
        } catch (deleteError) {
            console.error("Erreur lors de la suppression de la région", deleteError);
            setError(deleteError?.status === 404
                ? "Cette région n'existe plus."
                : deleteError?.status === 403
                    ? "Vous n'avez pas la permission de supprimer cette région."
                    : deleteError?.message || "La région n'a pas pu être supprimée.");
        }
    };

    return (
        <main className="w-full px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
                <h1 className="text-2xl font-bold">Gestion des régions</h1>
                <button
                    type="button"
                    className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
                    onClick={() => navigate("/admin/regions/add")}
                >
                    Ajouter une région
                </button>
            </div>

            <form onSubmit={handleSearch} className="mb-5 flex gap-2">
                <input
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Rechercher une région"
                    aria-label="Rechercher une région"
                    className="min-w-0 flex-1 rounded border border-gray-300 px-3 py-2"
                />
                <button type="submit" className="rounded bg-cyan-700 px-4 py-2 font-semibold text-white">
                    Rechercher
                </button>
            </form>

            {error && <p role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">{error}</p>}
            {loading ? (
                <p>Chargement des régions...</p>
            ) : regions.length === 0 ? (
                <p>Aucune région trouvée.</p>
            ) : (
                <div className="h-[50vh] overflow-x-auto overflow-y-scroll rounded-lg shadow">
                    <table className="w-full min-w-[640px] table-auto">
                        <thead className="bg-gray-100 text-left">
                            <tr>
                                <th className="px-4 py-3">Nom</th>
                                <th className="px-4 py-3">Pays associés</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {regions.map((region) => (
                                <tr key={region.id}>
                                    <td className="px-4 py-3">{region.name}</td>
                                    <td className="px-4 py-3">
                                        {(region.countries || [])
                                            .map((item) => item.country || item.country_code)
                                            .join(", ") || "Aucun pays"}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3">
                                        <button
                                            type="button"
                                            className="mr-3 font-semibold text-blue-700 hover:underline"
                                            onClick={() => navigate(`/admin/regions/edit/${region.id}`)}
                                        >
                                            Modifier
                                        </button>
                                        <button
                                            type="button"
                                            className="font-semibold text-red-700 hover:underline"
                                            onClick={() => handleDelete(region)}
                                        >
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && count > 0 && (
                <Pagination
                    itemsPerPage={ITEMS_PER_PAGE}
                    totalItems={count}
                    currentPage={page}
                    paginate={setPage}
                />
            )}
        </main>
    );
}
